import { supabase } from './supabase';
import { logger } from '../utils/logger';

/**
 * Admin Service with Secure Edge Function Integration
 *
 * Uses the admin-operations Edge Function which:
 * ✅ Uses service role key for elevated permissions (bypasses RLS)
 * ✅ Verifies admin status server-side
 * ✅ Logs all admin actions to audit_logs
 * ✅ Provides access to sensitive data (user emails)
 */

export interface ReportWithProfiles {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  reason: string;
  description?: string;
  created_at: string;
  status: 'pending' | 'reviewed' | 'resolved';
  admin_notes?: string;
  reporter?: {
    username: string;
    display_name: string;
  };
  reported_user?: {
    username: string;
    display_name: string;
    email?: string;
  };
}

export interface ReportStats {
  total: number;
  pending: number;
  reviewed: number;
  resolved: number;
  last24Hours: number;
}

/**
 * Check if current user is an admin
 * Checks JWT claims first (fast), falls back to database (reliable)
 */
export async function isAdmin(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // Try JWT claims first (fastest - no database query)
    const claims = session.user.app_metadata || {};
    const jwtAdminClaim = claims.is_admin ?? session.user.user_metadata?.is_admin;

    if (jwtAdminClaim === true) {
      logger.log('✅ Admin status from JWT claims');
      return true;
    }

    // Fallback: Check database (needed until JWT hook is working)
    logger.warn('⚠️ JWT claim not found, checking database');
    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', session.user.id)
      .single();

    if (error) {
      logger.error('Error checking admin status from database:', error);
      return false;
    }

    const isAdminFromDB = data?.is_admin === true;
    logger.log(`Admin status from database: ${isAdminFromDB}`);
    return isAdminFromDB;
  } catch (error) {
    logger.error('Error in isAdmin:', error);
    return false;
  }
}

/**
 * Get all reports (admin only) - uses Edge Function for rate limiting
 */
export async function getAllReports(
  status?: 'pending' | 'reviewed' | 'resolved'
): Promise<{ success: boolean; reports?: ReportWithProfiles[]; error?: string }> {
  try {
    // Client-side check (server will verify again)
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Call admin-operations Edge Function
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'get_reports',
        status: status,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from admin-operations Edge Function:', result.error);
      return { success: false, error: result.error || 'Failed to fetch reports' };
    }

    return { success: true, reports: result.reports };
  } catch (error) {
    logger.error('Error in getAllReports:', error);
    return { success: false, error: 'Failed to fetch reports' };
  }
}

/**
 * Update report status (admin only) - uses Edge Function for rate limiting
 */
export async function updateReportStatus(
  reportId: string,
  status: 'pending' | 'reviewed' | 'resolved',
  adminNotes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Client-side check (server will verify again)
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Call admin-operations Edge Function
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'update_report',
        reportId: reportId,
        status: status,
        adminNotes: adminNotes,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from admin-operations Edge Function:', result.error);
      return { success: false, error: result.error || 'Failed to update report' };
    }

    logger.info('Report updated successfully');

    return { success: true };
  } catch (error) {
    logger.error('Error in updateReportStatus:', error);
    return { success: false, error: 'Failed to update report' };
  }
}

/**
 * Get report statistics (admin only) - uses Edge Function for rate limiting
 */
export async function getReportStats(): Promise<{ success: boolean; stats?: ReportStats; error?: string }> {
  try {
    // Client-side check (server will verify again)
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Call admin-operations Edge Function
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'get_stats',
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from admin-operations Edge Function:', result.error);
      return { success: false, error: result.error || 'Failed to fetch stats' };
    }

    return { success: true, stats: result.stats };
  } catch (error) {
    logger.error('Error in getReportStats:', error);
    return { success: false, error: 'Failed to fetch stats' };
  }
}

/**
 * Ban a user (admin only) - uses Edge Function for rate limiting
 */
export async function banUser(
  userId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Client-side check (server will verify again)
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Call admin-operations Edge Function
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'ban_user',
        userId: userId,
        reason: reason,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from admin-operations Edge Function:', result.error);
      return { success: false, error: result.error || 'Failed to ban user' };
    }

    logger.info('User banned successfully');

    return { success: true };
  } catch (error) {
    logger.error('Error in banUser:', error);
    return { success: false, error: 'Failed to ban user' };
  }
}

/**
 * Delete a report (admin only) - uses Edge Function for rate limiting
 */
export async function deleteReport(
  reportId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Client-side check (server will verify again)
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Call admin-operations Edge Function
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'delete_report',
        reportId: reportId,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from admin-operations Edge Function:', result.error);
      return { success: false, error: result.error || 'Failed to delete report' };
    }

    logger.info('Report deleted successfully');

    return { success: true };
  } catch (error) {
    logger.error('Error in deleteReport:', error);
    return { success: false, error: 'Failed to delete report' };
  }
}

/**
 * Get user email (admin only)
 * Uses Edge Function with service role to access auth.users table
 */
export async function getUserEmail(
  userId: string
): Promise<{ success: boolean; email?: string; error?: string }> {
  try {
    // Client-side check (server will verify again)
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: 'Not authenticated' };
    }

    logger.info(`Fetching email for user ${userId}...`);

    // Call the admin-operations Edge Function
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get_user_email',
          data: { userId },
        }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      logger.error('Error fetching user email:', result);
      return {
        success: false,
        error: result.error || 'Failed to fetch user email',
      };
    }

    return {
      success: true,
      email: result.email,
    };
  } catch (error) {
    logger.error('Error in getUserEmail:', error);
    return { success: false, error: 'Failed to fetch user email' };
  }
}

/**
 * Get all account deletion requests (admin only) - uses Edge Function
 */
export async function getDeletionRequests(): Promise<{ success: boolean; requests?: any[]; error?: string }> {
  try {
    const admin = await isAdmin();
    if (!admin) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Call admin-operations Edge Function
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'get_deletion_requests',
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from admin-operations Edge Function:', result.error);
      return { success: false, error: result.error || 'Failed to fetch deletion requests' };
    }

    return { success: true, requests: result.requests };
  } catch (error) {
    logger.error('Error in getDeletionRequests:', error);
    return { success: false, error: 'Failed to fetch deletion requests' };
  }
}